import React, { useState } from "react";

import * as Sentry from "@sentry/react";
import { isEmpty, isEqual } from "lodash/fp";

import { Divider } from "@mui/material";

import useCheckout from "@app/analytics/account/Billing/hooks/useCheckout";
import usePaymentMethod from "@app/analytics/account/Billing/hooks/usePaymentMethod";
import useStripeLoadFailed from "@app/analytics/account/Billing/hooks/useStripeLoadFailed";
import type { PaymentFormElementOptions } from "@app/analytics/account/Billing/types";
import * as BillingUtils from "@app/analytics/account/Billing/utils";
import { showToast } from "@app/common/hooks/useToasts";
import type {
  CheckoutBillingInfo,
  InitiateCheckoutResponse,
} from "@app/common/types";
import logger from "@app/utils/logger";

import Stack from "@rui/Stack";

import AddressModule from "./AddressModule";
import {
  INVALID_ELEMENT_STATE_ERROR,
  PURCHASE_SUBMIT_BUTTON_ID,
} from "./constants";
import ExternalPaymentNeeded from "./ExternalPaymentNeeded";
import PaymentModule from "./PaymentModule";
import {
  CheckoutButton,
  PanelContentInnerContainer,
  PanelContentOuterContainer,
  PanelMainContent,
  PurchaseHeading,
} from "../styles";

export interface BillingPurchaseFormProps {
  isLoading: boolean;
  onChangeBillingInfo: (
    checkoutBillingInfo: CheckoutBillingInfo,
    options?: {
      onSuccess: (response: InitiateCheckoutResponse) => void;
      onError: (error: Error) => void;
    }
  ) => void;
  onPurchase: () => void;
  paymentInfoElementOptions: PaymentFormElementOptions | null;
  paymentConfirmReturnUrl: string;
  submitCta: string;
}

const BillingPurchaseForm = ({
  isLoading,
  paymentConfirmReturnUrl,
  paymentInfoElementOptions,
  onPurchase,
  onChangeBillingInfo,
  submitCta,
}: BillingPurchaseFormProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const [isPurchasing, setIsPurchasing] = useState(false);

  const { stripeLoadFailed, onStripeLoadFailed } = useStripeLoadFailed();
  const { paymentMethod, paymentMethodIsLoading } = usePaymentMethod();
  const { checkoutBillingInfo, isCheckoutBillingInfoLoading } = useCheckout();

  const existingSavedAddress =
    BillingUtils.getFormattedAddress(checkoutBillingInfo);
  const hasSavedAddress = !isEmpty(existingSavedAddress);
  const [isEditingAddress, setIsEditingAddress] = useState(!hasSavedAddress);

  const handleSaveBillingInfo = (updatedBillingInfo: CheckoutBillingInfo) => {
    if (!checkoutBillingInfo) return;

    if (isEqual(updatedBillingInfo, checkoutBillingInfo)) {
      setIsEditingAddress(false);
      return;
    }

    setIsSaving(true);
    onChangeBillingInfo(updatedBillingInfo, {
      onSuccess: () => {
        setIsEditingAddress(false);
        setIsSaving(false);
      },
      onError: () => setIsSaving(false),
    });
  };

  const handleClickSubmit = async () => {
    const realSubmitButton = document.getElementById(PURCHASE_SUBMIT_BUTTON_ID);
    if (!realSubmitButton) {
      logger.error("no submit button found");
      showToast({
        severity: "error",
        message: INVALID_ELEMENT_STATE_ERROR,
      });
      Sentry.captureMessage("Stripe submit button not found");
      return;
    }

    setIsPurchasing(true);

    onPurchase();

    // programmatically click the button that's rendered alongside the payment element, since we want
    // the subscribe button to be at the bottom of the page to match the other steps, but the click
    // handler needs to have access to the `stripe` and `elements` objects, which can only be used within
    // the component that renders the PaymentElement (and cannot be used in a component that renders the
    // `StripeElements` parent component)
    realSubmitButton.click();
  };

  const handlePurchaseConfirmSettled = (error?: string) => {
    setIsPurchasing(false);
    if (error) {
      showToast({
        severity: "error",
        message: error,
      });
    }
  };

  // TODO (ENG-8647) - allow editing payment method

  const billingInfoIncomplete = !hasSavedAddress || !checkoutBillingInfo;
  const paymentInfoIncomplete =
    paymentMethodIsLoading || paymentInfoElementOptions?.clientSecret == null;
  const submitDisabled =
    isLoading ||
    billingInfoIncomplete ||
    paymentInfoIncomplete ||
    isEditingAddress;

  // true if stripe failed to load and
  // either the payment method
  // or billing address is missing
  const needsPaymentPortalLink =
    stripeLoadFailed &&
    !paymentMethodIsLoading &&
    (!paymentMethod || isEmpty(checkoutBillingInfo?.address));

  if (needsPaymentPortalLink) {
    return <ExternalPaymentNeeded />;
  }

  return (
    <PanelContentOuterContainer variant="center">
      <PanelContentInnerContainer variant="center" spacing={2}>
        <PanelMainContent variant="center">
          <Stack spacing={3} sx={{ width: "100%" }}>
            <Stack spacing={2}>
              <PurchaseHeading>Billing Address</PurchaseHeading>
              <AddressModule
                open={isEditingAddress || !checkoutBillingInfo?.address}
                loading={isCheckoutBillingInfoLoading}
                saving={isSaving}
                checkoutBillingInfo={checkoutBillingInfo ?? null}
                onEdit={() => setIsEditingAddress(true)}
                onSave={handleSaveBillingInfo}
                onLoadError={onStripeLoadFailed}
              />
            </Stack>
            <Divider />
            <Stack spacing={2}>
              <PurchaseHeading>Payment Method</PurchaseHeading>
              {checkoutBillingInfo && (
                <PaymentModule
                  loading={isSaving || !paymentInfoElementOptions?.clientSecret}
                  paymentMethod={paymentMethod}
                  paymentConfirmReturnUrl={paymentConfirmReturnUrl}
                  onSubmitSettled={handlePurchaseConfirmSettled}
                  onLoadError={onStripeLoadFailed}
                  billingInfo={checkoutBillingInfo}
                  paymentInfoElementOptions={paymentInfoElementOptions}
                />
              )}
            </Stack>
          </Stack>
        </PanelMainContent>
        <CheckoutButton
          disabled={submitDisabled}
          onClick={handleClickSubmit}
          loading={isPurchasing}
        >
          {submitCta}
        </CheckoutButton>
      </PanelContentInnerContainer>
    </PanelContentOuterContainer>
  );
};

export default BillingPurchaseForm;
